import { useMemo, useState } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { startOfWeek, addDays, format, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, ChevronLeft, ChevronRight, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useAppointments } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import type { Appointment, CoverageVerdict } from '@/types';
import NewAppointmentModal from './NewAppointmentModal';
import AppointmentDetailModal from './AppointmentDetailModal';

// ============================================================================
// Constantes de grille
// ============================================================================

const DAY_COUNT = 6; // lundi → samedi
const HOUR_START = 8;
const HOUR_END = 20;
const SLOT_MIN = 30; // granularité d'une ligne
const ROW_PX = 26; // hauteur d'une ligne de 30 min
const PX_PER_MIN = ROW_PX / SLOT_MIN;

const ROWS = ((HOUR_END - HOUR_START) * 60) / SLOT_MIN;

export const TYPE_LABELS: Record<string, string> = {
  VACCINATION: 'Vaccination',
  TROD: 'TROD',
  ENTRETIEN_PHARMACEUTIQUE: 'Entretien pharmaceutique',
  BILAN_PREVENTION: 'Bilan de prévention',
  PDA: 'PDA',
  AUTRE: 'Autre',
};

export const SKILL_LABELS: Record<string, string> = {
  COMPTOIR: 'Comptoir',
  VACCINATION: 'Vaccination',
  ENTRETIEN: 'Entretien',
  BILAN: 'Bilan',
  PREPARATION: 'Préparation',
  BACKOFFICE_PHARMA: 'Back-office pharma',
  BACKOFFICE_TECHNIQUE: 'Back-office technique',
  TROD: 'TROD',
  PDA: 'PDA',
  PREPARATOIRE: 'Préparatoire',
};

/** Palette par verdict de couverture — le rouge est réservé à l'alerte. */
export const VERDICT_STYLE: Record<
  CoverageVerdict,
  { block: string; dot: string; label: string }
> = {
  ALERT: {
    block: 'bg-red-50 border-red-300 text-red-900 hover:bg-red-100',
    dot: 'bg-red-500',
    label: 'Aucune personne qualifiée',
  },
  REASSIGN: {
    block: 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100',
    dot: 'bg-amber-500',
    label: 'À affecter',
  },
  FORCED: {
    block: 'bg-violet-50 border-violet-300 text-violet-900 hover:bg-violet-100',
    dot: 'bg-violet-500',
    label: 'Désignation exceptionnelle',
  },
  OK: {
    block: 'bg-emerald-50 border-emerald-300 text-emerald-900 hover:bg-emerald-100',
    dot: 'bg-emerald-500',
    label: 'Couvert',
  },
  NONE: {
    block: 'bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100',
    dot: 'bg-slate-400',
    label: 'Sans compétence requise',
  },
};

const CANCELLED_STYLE = 'bg-slate-50 border-slate-200 text-slate-400 line-through hover:bg-slate-100';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Convention de temps du produit : les horaires sont de l'heure locale
 * pharmacie, transportée en UTC (comme les dates de planning, à minuit UTC).
 * On lit donc `startAt` avec les getters UTC — sinon un RDV de 9 h s'afficherait
 * à 11 h l'été.
 */
export function slotMinutes(iso: string): number {
  const d = new Date(iso);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

export function dayKeyOf(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

export function formatSlot(iso: string): string {
  const m = slotMinutes(iso);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}h${String(m % 60).padStart(2, '0')}`;
}

function verdictOf(appt: Appointment): CoverageVerdict {
  return appt.coverage?.verdict ?? 'NONE';
}

/**
 * Libellé affiché : celui de la prestation du catalogue (« TROD angine »),
 * avec repli sur la famille générique pour les RDV créés avant le catalogue.
 */
export function appointmentLabel(appt: Appointment): string {
  return appt.catalogItem?.label ?? TYPE_LABELS[appt.type] ?? appt.type;
}

// ============================================================================
// Page
// ============================================================================

export default function AppointmentsPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [creating, setCreating] = useState<{ dayKey: string; time: string } | null>(null);
  const [selected, setSelected] = useState<Appointment | null>(null);

  const weekStart = useMemo(
    () => addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7),
    [weekOffset]
  );
  const days = useMemo(
    () => Array.from({ length: DAY_COUNT }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Bornes envoyées au backend : on couvre la journée entière du samedi.
  const from = useMemo(() => `${format(weekStart, 'yyyy-MM-dd')}T00:00:00.000Z`, [weekStart]);
  const to = useMemo(
    () => `${format(addDays(weekStart, DAY_COUNT - 1), 'yyyy-MM-dd')}T23:59:59.999Z`,
    [weekStart]
  );

  const { data: appointments, isLoading } = useAppointments(from, to);

  const alertCount = useMemo(
    () =>
      (appointments ?? []).filter(
        (a) => a.status !== 'CANCELLED' && verdictOf(a) === 'ALERT'
      ).length,
    [appointments]
  );

  // Regroupement par jour pour le positionnement dans la grille.
  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of appointments ?? []) {
      const k = dayKeyOf(a.startAt);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    }
    return map;
  }, [appointments]);

  const hourLabels = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

  return (
    <div className="flex flex-col h-full">
      {/* En-tête */}
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-[22px] font-semibold">Rendez-vous métier <PageHelp topic="appointments" /></h1>
          <div className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-2">
            <span>
              {(appointments ?? []).filter((a) => a.status !== 'CANCELLED').length} rendez-vous
            </span>
            {alertCount > 0 && (
              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                <AlertTriangle size={12} />
                {alertCount} sans personne qualifiée
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setWeekOffset((w) => w - 1)}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
              aria-label="Semaine précédente"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setWeekOffset(0)}
              className="text-[12px] font-medium text-slate-600 px-2 py-1 rounded-lg hover:bg-slate-100 tabular-nums"
            >
              {format(weekStart, 'd MMM', { locale: fr })} –{' '}
              {format(addDays(weekStart, DAY_COUNT - 1), 'd MMM yyyy', { locale: fr })}
            </button>
            <button
              onClick={() => setWeekOffset((w) => w + 1)}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
              aria-label="Semaine suivante"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() =>
              setCreating({ dayKey: format(days[0], 'yyyy-MM-dd'), time: '09:00' })
            }
          >
            Nouveau rendez-vous
          </Button>
        </div>
      </div>

      {/* Légende */}
      <div className="px-8 py-2 border-b border-slate-100 flex items-center gap-4 text-[11px] text-slate-500 shrink-0">
        {(['OK', 'ALERT', 'REASSIGN', 'FORCED'] as CoverageVerdict[]).map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${VERDICT_STYLE[v].dot}`} />
            {VERDICT_STYLE[v].label}
          </span>
        ))}
      </div>

      {/* Grille */}
      <div className="flex-1 overflow-auto">
        {isLoading && <div className="p-6 text-[12px] text-slate-500">Chargement…</div>}

        <div className="min-w-[840px] px-8 py-4">
          {/* Bandeau des jours */}
          <div className="grid" style={{ gridTemplateColumns: `56px repeat(${DAY_COUNT}, 1fr)` }}>
            <div />
            {days.map((d) => {
              const today = isSameDay(d, new Date());
              return (
                <div
                  key={d.toISOString()}
                  className={`text-center pb-2 border-b ${
                    today ? 'border-slate-900' : 'border-slate-200'
                  }`}
                >
                  <div
                    className={`text-[11px] uppercase tracking-wide ${
                      today ? 'text-slate-900 font-semibold' : 'text-slate-400'
                    }`}
                  >
                    {format(d, 'EEE', { locale: fr })}
                  </div>
                  <div
                    className={`text-[15px] tabular-nums ${
                      today ? 'font-semibold text-slate-900' : 'text-slate-600'
                    }`}
                  >
                    {format(d, 'd')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Corps : colonne d'heures + 6 colonnes de jours */}
          <div
            className="grid relative"
            style={{ gridTemplateColumns: `56px repeat(${DAY_COUNT}, 1fr)` }}
          >
            {/* Heures */}
            <div className="relative" style={{ height: ROWS * ROW_PX }}>
              {hourLabels.map((h, i) => (
                <div
                  key={h}
                  className="absolute right-2 text-[11px] text-slate-400 tabular-nums -translate-y-1/2"
                  style={{ top: i * 2 * ROW_PX }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Jours */}
            {days.map((d) => {
              const dayKey = format(d, 'yyyy-MM-dd');
              const dayAppts = byDay.get(dayKey) ?? [];
              return (
                <div
                  key={dayKey}
                  className="relative border-l border-slate-100"
                  style={{ height: ROWS * ROW_PX }}
                >
                  {/* Lignes de fond + création au clic sur un créneau vide */}
                  {Array.from({ length: ROWS }, (_, r) => {
                    const min = HOUR_START * 60 + r * SLOT_MIN;
                    const time = `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(
                      min % 60
                    ).padStart(2, '0')}`;
                    return (
                      <button
                        key={r}
                        onClick={() => setCreating({ dayKey, time })}
                        title={`Nouveau rendez-vous à ${time}`}
                        className={`absolute left-0 right-0 hover:bg-slate-50 ${
                          r % 2 === 0 ? 'border-t border-slate-100' : 'border-t border-slate-50'
                        }`}
                        style={{ top: r * ROW_PX, height: ROW_PX }}
                      />
                    );
                  })}

                  {/* Blocs RDV */}
                  {dayAppts.map((a) => {
                    const startMin = slotMinutes(a.startAt);
                    const top = (startMin - HOUR_START * 60) * PX_PER_MIN;
                    const height = Math.max(a.durationMinutes * PX_PER_MIN, 20);
                    if (top < 0 || top > ROWS * ROW_PX) return null;

                    const cancelled = a.status === 'CANCELLED';
                    const style = cancelled
                      ? CANCELLED_STYLE
                      : VERDICT_STYLE[verdictOf(a)].block;

                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelected(a)}
                        className={`absolute left-1 right-1 rounded-md border px-1.5 py-0.5 text-left overflow-hidden transition-colors ${style}`}
                        style={{ top, height }}
                      >
                        <div className="flex items-center gap-1 text-[10px] font-semibold leading-tight truncate">
                          {!cancelled && verdictOf(a) === 'ALERT' && (
                            <AlertTriangle size={10} className="shrink-0" />
                          )}
                          {!cancelled && verdictOf(a) === 'FORCED' && (
                            <ShieldCheck size={10} className="shrink-0" />
                          )}
                          <span className="truncate">{appointmentLabel(a)}</span>
                        </div>
                        {height > 30 && (
                          <div className="text-[9px] leading-tight truncate opacity-80 tabular-nums">
                            {formatSlot(a.startAt)}
                            {a.employee
                              ? ` · ${a.employee.firstName} ${a.employee.lastName[0]}.`
                              : ' · non affecté'}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {!isLoading && (appointments ?? []).length === 0 && (
            <div className="text-center py-8 text-[12px] text-slate-400">
              Aucun rendez-vous cette semaine — cliquez sur un créneau pour en créer un.
            </div>
          )}
        </div>
      </div>

      {creating && (
        <NewAppointmentModal
          dayKey={creating.dayKey}
          time={creating.time}
          onClose={() => setCreating(null)}
        />
      )}
      {selected && (
        <AppointmentDetailModal
          appointment={
            // On relit depuis la liste fraîche pour que la modale reflète le
            // recalcul de couverture après un forçage.
            (appointments ?? []).find((a) => a.id === selected.id) ?? selected
          }
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
